import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useProducts } from '../../hooks/useProducts';
import { addToCart, getCart } from '../../utils/cart';
import { useSEO, generateOrganizationSchema } from '../../utils/seo';
import BlogPreview from './components/BlogPreview';
import QuickTracking from './components/QuickTracking';
import WishlistButton from '../../components/base/WishlistButton';
import CartNotification from '../../components/feature/CartNotification';

const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  for (let i = 0; i < fullStars; i++) {
    stars.push(<i key={`full-${i}`} className="ri-star-fill text-[#2E7D32] text-xs"></i>);
  }
  if (hasHalf) {
    stars.push(<i key="half" className="ri-star-half-fill text-[#2E7D32] text-xs"></i>);
  }
  const remaining = 5 - fullStars - (hasHalf ? 1 : 0);
  for (let i = 0; i < remaining; i++) {
    stars.push(<i key={`empty-${i}`} className="ri-star-line text-gray-300 text-xs"></i>);
  }
  return stars;
};

export default function HomePage() {
  const { products, loading, error } = useProducts();
  const featuredProducts = products.slice(0, 3);
  const [notifiedItem, setNotifiedItem] = useState<import('../../utils/cart').CartItem | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const navigate = useNavigate();

  useSEO({
    title: 'Daliah Secret | Luxury Perfumes & Hair Oils',
    description: 'Discover Daliah Secret\'s exquisite collection of handcrafted perfumes and nourishing hair oils. Premium natural ingredients, expertly blended for timeless elegance. Shop luxury fragrances and hair care products with free shipping on orders over $100.',
    keywords: 'Daliah Secret, luxury perfumes, hair oils, natural fragrances, premium cosmetics, handcrafted perfumes',
    canonical: '/',
    ogType: 'website',
    schema: generateOrganizationSchema(),
  });

  const handleAddToCart = (e: React.MouseEvent, product: typeof products[0]) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if sold out
    if (product.stock_quantity === 0) {
      setToastMessage('This product is out of stock');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    
    const result = addToCart(
      {
        id: parseInt(product.id),
        name: product.name,
        price: product.price,
        image: product.image,
      },
      1,
      product.stock_quantity
    );
    
    if (result.success && result.item) {
      setNotifiedItem(result.item);
    } else {
      setToastMessage(result.message || 'Error adding to cart');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 px-6 py-4 rounded-lg shadow-lg bg-red-500 animate-slide-in">
          <div className="flex items-center gap-3 text-white">
            <i className="ri-error-warning-line text-xl"></i>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Featured Collection */}
      <section className="py-10 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-4 font-serif">
              Collection Vedette
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Nos produits les plus populaires, soigneusement sélectionnés pour vous
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8 mb-6 sm:mb-12">
            {featuredProducts.map((product) => {
              const isSoldOut = product.stock_quantity === 0;
              const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= 5;
              
              return (
                <div
                  key={product.id}
                  className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <div className="relative h-44 sm:h-80 overflow-hidden bg-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    {isSoldOut ? (
                      <span className="absolute top-2 left-2 sm:top-4 sm:left-4 px-2 sm:px-3 py-0.5 sm:py-1 bg-red-500 text-white text-[10px] sm:text-xs font-semibold rounded-full whitespace-nowrap">
                        OUT OF STOCK
                      </span>
                    ) : isLowStock ? (
                      <span className="absolute top-2 left-2 sm:top-4 sm:left-4 px-2 sm:px-3 py-0.5 sm:py-1 bg-orange-500 text-white text-[10px] sm:text-xs font-semibold rounded-full whitespace-nowrap">
                        LIMITED STOCK
                      </span>
                    ) : product.badge && (
                      <span className="absolute top-2 left-2 sm:top-4 sm:left-4 px-2 sm:px-3 py-0.5 sm:py-1 bg-[#C4A574] text-white text-[10px] sm:text-xs font-semibold rounded-full whitespace-nowrap">
                        {product.badge}
                      </span>
                    )}
                    <WishlistButton
                      product={{
                        id: parseInt(product.id),
                        name: product.name,
                        price: product.price,
                        image: product.image,
                        category: product.category,
                      }}
                    />
                  </div>
                  <div className="p-3 sm:p-6">
                    <div className="flex items-start justify-between mb-1 sm:mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-0.5 sm:mb-1 group-hover:text-[#C4A574] transition-colors truncate">
                          {product.name}
                        </h3>
                        <p className="text-[10px] sm:text-sm text-gray-500 truncate">{product.category}</p>
                      </div>
                    </div>
                    
                    {/* Stock Info */}
                    <div className="mb-2 sm:mb-3">
                      {isSoldOut ? (
                        <span className="text-[10px] sm:text-xs text-red-600 font-medium">Out of stock</span>
                      ) : isLowStock ? (
                        <span className="text-[10px] sm:text-xs text-orange-600 font-medium">
                          Only {product.stock_quantity} in stock
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs text-green-600 font-medium">
                          {product.stock_quantity} in stock
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-base sm:text-2xl font-bold text-gray-900">${product.price}</span>
                        {product.originalPrice && (
                          <span className="ml-1 sm:ml-2 text-xs sm:text-base text-gray-400 line-through">
                            ${product.originalPrice}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={isSoldOut}
                        className={`w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-colors ${
                          isSoldOut 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-[#C4A574] text-white hover:bg-[#B39565] cursor-pointer'
                        }`}
                      >
                        <i className="ri-shopping-bag-line text-sm sm:text-xl"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="inline-block px-6 sm:px-10 py-3 sm:py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-colors whitespace-nowrap text-sm sm:text-base"
            >
              Voir tous les produits
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-10 sm:py-16 bg-[#F1F8E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8">
            <div className="flex sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 sm:mx-auto sm:mb-4 shadow-sm">
                <i className="ri-truck-line text-xl sm:text-2xl text-[#2E7D32]"></i>
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 sm:mb-2">Livraison Gratuite</h3>
                <p className="text-xs sm:text-sm text-gray-600">Sur les commandes de $200 et plus</p>
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 sm:mx-auto sm:mb-4 shadow-sm">
                <i className="ri-shield-check-line text-xl sm:text-2xl text-[#2E7D32]"></i>
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 sm:mb-2">Paiement Sécurisé</h3>
                <p className="text-xs sm:text-sm text-gray-600">Transactions 100% sécurisées</p>
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 sm:mx-auto sm:mb-4 shadow-sm">
                <i className="ri-customer-service-2-line text-xl sm:text-2xl text-[#2E7D32]"></i>
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 sm:mb-2">Support 24/7</h3>
                <p className="text-xs sm:text-sm text-gray-600">Service client disponible à tout moment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tracking */}
      <QuickTracking />

      {/* Brand Story */}
      <section className="py-10 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-6 font-serif">
                Notre Histoire
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 mb-3 sm:mb-6 leading-relaxed">
                Daliah Secret est née d&apos;une passion pour la beauté naturelle et l&apos;élégance intemporelle. 
                Nous croyons que chaque personne mérite de se sentir belle et confiante dans sa peau.
              </p>
              <p className="text-sm sm:text-lg text-gray-600 mb-5 sm:mb-8 leading-relaxed">
                Nos produits sont soigneusement formulés avec des ingrédients naturels de la plus haute 
                qualité, pour vous offrir une expérience de beauté luxueuse et efficace.
              </p>
              <Link
                to="/about"
                className="inline-block px-6 sm:px-10 py-3 sm:py-4 bg-[#C4A574] text-white font-semibold rounded-full hover:bg-[#B39565] transition-colors whitespace-nowrap text-sm sm:text-base"
              >
                En savoir plus
              </Link>
            </div>
            <div className="order-1 lg:order-2 h-52 sm:h-96 lg:h-[500px] rounded-xl sm:rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/f2371b2c54714b47b1298e930c974864.jpg"
                alt="Notre Histoire"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <BlogPreview />

      {/* Cart Notification */}
      <CartNotification item={notifiedItem} onClose={() => setNotifiedItem(null)} />

      <Footer />
    </div>
  );
}
